import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useNotifications } from '../../context';
import { getProfile, type Demographics } from '../../api/profile';
import { deleteAccount } from '../../api/account';
import { ActivityIndicator, Button } from '../../components/ui';
import styles from './ProfilePage.module.css';

export function ProfilePage() {
  const navigate = useNavigate();
  const { email, loginKey, logout } = useAuth();
  const { notificationPermission, requestPermission } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<Demographics>({});

  const loadProfile = useCallback(async () => {
    if (!loginKey) return;

    setLoading(true);
    const response = await getProfile(loginKey);
    setLoading(false);

    if (!response.ok || response.data?.error) {
      alert('Unable to load profile.');
      return;
    }

    if (response.data?.demographics) {
      setProfile(response.data.demographics);
    }
  }, [loginKey]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleLogout = () => {
    logout();
  };

  const handleDeleteAccount = async () => {
    if (!loginKey) return;

    const confirmed = window.confirm(
      'Are you sure? You will lose access to any unspent coins. A confirmation email will be sent.'
    );
    if (!confirmed) return;

    setLoading(true);
    const response = await deleteAccount(loginKey);
    setLoading(false);

    if (response.ok && response.data?.success) {
      alert('A confirmation email has been sent. Click the link to complete deletion.');
      logout();
    } else {
      alert(response.data?.error?.message || 'Failed to delete account. Please try again.');
    }
  };

  return (
    <div className={styles.container}>
      <ActivityIndicator visible={loading} />

      <p className={styles.email}>{email}</p>

      {(profile.first_name || profile.last_name) && (
        <p className={styles.info}>
          {profile.first_name} {profile.last_name}
        </p>
      )}

      {profile.phone && <p className={styles.info}>{profile.phone}</p>}

      {(profile.address_1 || profile.address_2 || profile.city || profile.state || profile.zip) && (
        <>
          <p className={styles.label}>Address:</p>
          {profile.address_1 && <p className={styles.info}>{profile.address_1}</p>}
          {profile.address_2 && <p className={styles.info}>{profile.address_2}</p>}
          {(profile.city || profile.state || profile.zip) && (
            <p className={styles.info}>
              {profile.city} {profile.state}
              {(profile.city || profile.state) && profile.zip ? ', ' : ''} {profile.zip}
            </p>
          )}
        </>
      )}

      <div className={styles.spacer} />

      <div className={styles.buttonRow}>
        <Button onClick={() => navigate('/edit-profile', { state: { profile } })}>
          Edit Profile
        </Button>
        {notificationPermission !== 'granted' && notificationPermission !== 'unsupported' && (
          <Button theme="outline" onClick={requestPermission}>
            Enable Notifications
          </Button>
        )}
        <Button theme="outline" onClick={handleLogout}>
          Log Out
        </Button>
        <button className={styles.deleteButton} onClick={handleDeleteAccount}>
          Delete Account
        </button>
      </div>
    </div>
  );
}

export default ProfilePage;
